// src/registered/site-admin/components/Workflow-esg/services/TemplateManagementService.js

import TemplateStorageService from "./TemplateStorageService";
import TemplateValidationService from "./TemplateValidationService";

/**
 
 * 
 * ================================================================================================
 * PURPOSE:
 *   Core orchestration service for ESG Template Management.
 *
 * RESPONSIBILITIES:
 *   - Validate template payloads before persistence
 *   - Delegate CRUD operations to storage service
 *   - Apply business rules for creation, update, deletion, and duplication
 *   - Normalize template payloads into a stable working shape
 *   - Ensure all operations are scoped to a selected root ESG taxonomy file
 *
 * NOTES:
 *   - Controllers should call this service, not storage directly
 *   - Views should never contain template business logic
 *   - This is the "bedrock" service for template lifecycle management
 * ================================================================================================
 */
class TemplateManagementService {
  /**
   * @param {Object} [dependencies]
   * @param {TemplateStorageService} [dependencies.storageService]
   * @param {TemplateValidationService} [dependencies.validationService]
   */
  constructor(dependencies = {}) {
    this.storageService =
      dependencies.storageService || new TemplateStorageService();

    this.validationService =
      dependencies.validationService || new TemplateValidationService();
  }

  // ===============================================================================================
  // PUBLIC API
  // ===============================================================================================

  /**
   * Load all templates for the selected root ESG JSON.
   * @param {Object} rootFileContext
   * @returns {Promise<Array>}
   */
  async getAllTemplates(rootFileContext) {
    this.#requireRootFileContext(rootFileContext);

    const templates =
      await this.storageService.getAllTemplates(rootFileContext);

    return Array.isArray(templates) ? templates : [];
  }

  /**
   * Load a single template by ID for the selected root ESG JSON.
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @returns {Promise<Object|null>}
   */
  async getTemplateById(rootFileContext, templateId) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireTemplateId(templateId);

    const template = await this.storageService.getTemplateById(
      rootFileContext,
      templateId,
    );

    return template || null;
  }

  /**
   * Create a new template under the selected root ESG JSON context.
   * @param {Object} rootFileContext
   * @param {Object} templateDto
   * @returns {Promise<Object>}
   */
  async createTemplate(rootFileContext, templateDto) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireTemplatePayload(templateDto);

    const normalizedTemplate = this.#buildNewTemplate(templateDto);
    const validation =
      this.validationService.validateTemplate(normalizedTemplate);

    this.#throwIfInvalid(validation);

    const existingTemplates =
      await this.storageService.getAllTemplates(rootFileContext);

    this.#ensureNameIsNotDuplicated(
      normalizedTemplate.name,
      existingTemplates,
    );

    return await this.storageService.saveTemplate(
      rootFileContext,
      normalizedTemplate,
    );
  }

  /**
   * Update an existing template within the selected root ESG JSON context.
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @param {Object} templateDto
   * @returns {Promise<Object>}
   */
  async updateTemplate(rootFileContext, templateId, templateDto) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireTemplateId(templateId);
    this.#requireTemplatePayload(templateDto);

    const existingTemplate = await this.storageService.getTemplateById(
      rootFileContext,
      templateId,
    );

    if (!existingTemplate) {
      throw new Error(`Template not found for ID: ${templateId}`);
    }

    const mergedTemplate = this.#buildUpdatedTemplate(
      existingTemplate,
      templateDto,
    );

    const validation = this.validationService.validateTemplate(mergedTemplate);

    this.#throwIfInvalid(validation);

    const allTemplates =
      await this.storageService.getAllTemplates(rootFileContext);

    this.#ensureNameIsNotDuplicated(
      mergedTemplate.name,
      allTemplates,
      templateId,
    );

    return await this.storageService.updateTemplate(
      rootFileContext,
      templateId,
      mergedTemplate,
    );
  }

  /**
   * Delete a template within the selected root ESG JSON context.
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @returns {Promise<boolean>}
   */
  async deleteTemplate(rootFileContext, templateId) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireTemplateId(templateId);

    const existingTemplate = await this.storageService.getTemplateById(
      rootFileContext,
      templateId,
    );

    if (!existingTemplate) {
      throw new Error(`Cannot delete. Template not found for ID: ${templateId}`);
    }

    return await this.storageService.deleteTemplate(
      rootFileContext,
      templateId,
    );
  }

  /**
   * Duplicate a template within the selected root ESG JSON context.
   *
   * BUSINESS RULES:
   *   - new ID
   *   - "(Copy)" appended to name
   *   - version reset to 1
   *   - inactive by default
   *   - status reset to "draft"
   *   - nested section/element IDs regenerated where possible
   *
   * @param {Object} rootFileContext
   * @param {string} templateId
   * @returns {Promise<Object>}
   */
  async duplicateTemplate(rootFileContext, templateId) {
    this.#requireRootFileContext(rootFileContext);
    this.#requireTemplateId(templateId);

    const sourceTemplate = await this.storageService.getTemplateById(
      rootFileContext,
      templateId,
    );

    if (!sourceTemplate) {
      throw new Error(`Cannot duplicate. Template not found for ID: ${templateId}`);
    }

    const duplicate = this.#buildDuplicatedTemplate(sourceTemplate);
    const validation = this.validationService.validateTemplate(duplicate);

    this.#throwIfInvalid(validation);

    const existingTemplates =
      await this.storageService.getAllTemplates(rootFileContext);

    this.#ensureNameIsNotDuplicated(
      duplicate.name,
      existingTemplates,
    );

    return await this.storageService.saveTemplate(
      rootFileContext,
      duplicate,
    );
  }

  // ===============================================================================================
  // PRIVATE HELPERS
  // ===============================================================================================

  /**
   * Create a fully normalized new template object.
   * @param {Object} templateDto
   * @returns {Object}
   */
  #buildNewTemplate(templateDto) {
    const now = this.#nowIso();

    return {
      id: templateDto.id || this.#generateId(),
      name: this.#cleanString(templateDto.name),
      description: this.#cleanString(templateDto.description),
      templateType: this.#cleanString(templateDto.templateType),
      appliesTo: this.#cleanString(templateDto.appliesTo),
      reportingFrequency: this.#cleanString(templateDto.reportingFrequency),
      sections: this.#normalizeSections(templateDto.sections),
      taxonomyBindings: this.#normalizeTaxonomyBindings(
        templateDto.taxonomyBindings,
      ),
      version: this.#normalizeVersion(templateDto.version),
      isActive: this.#normalizeBoolean(templateDto.isActive, true),
      status: this.#cleanString(templateDto.status || "draft"),
      createdAt: templateDto.createdAt || now,
      updatedAt: now,
    };
  }

  /**
   * Merge an update payload onto an existing template.
   * @param {Object} existingTemplate
   * @param {Object} templateDto
   * @returns {Object}
   */
  #buildUpdatedTemplate(existingTemplate, templateDto) {
    return {
      ...existingTemplate,
      ...templateDto,
      id: existingTemplate.id,
      name: this.#cleanString(templateDto.name ?? existingTemplate.name),
      description: this.#cleanString(
        templateDto.description ?? existingTemplate.description,
      ),
      templateType: this.#cleanString(
        templateDto.templateType ?? existingTemplate.templateType,
      ),
      appliesTo: this.#cleanString(
        templateDto.appliesTo ?? existingTemplate.appliesTo,
      ),
      reportingFrequency: this.#cleanString(
        templateDto.reportingFrequency ?? existingTemplate.reportingFrequency,
      ),
      sections: this.#normalizeSections(
        templateDto.sections ?? existingTemplate.sections,
      ),
      taxonomyBindings: this.#normalizeTaxonomyBindings(
        templateDto.taxonomyBindings ?? existingTemplate.taxonomyBindings,
      ),
      version: this.#normalizeVersion(
        templateDto.version ?? existingTemplate.version,
      ),
      isActive: this.#normalizeBoolean(
        templateDto.isActive,
        existingTemplate.isActive,
      ),
      status: this.#cleanString(
        templateDto.status ?? existingTemplate.status ?? "draft",
      ),
      createdAt: existingTemplate.createdAt || this.#nowIso(),
      updatedAt: this.#nowIso(),
    };
  }

  /**
   * Create a duplicate template with regenerated IDs and reset metadata.
   * @param {Object} sourceTemplate
   * @returns {Object}
   */
  #buildDuplicatedTemplate(sourceTemplate) {
    const now = this.#nowIso();
    const duplicatedName = this.#buildCopyName(sourceTemplate.name);

    return {
      ...sourceTemplate,
      id: this.#generateId(),
      name: duplicatedName,
      sections: this.#cloneSectionsWithNewIds(sourceTemplate.sections),
      taxonomyBindings: this.#normalizeTaxonomyBindings(
        sourceTemplate.taxonomyBindings,
      ),
      version: 1,
      isActive: false,
      status: "draft",
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Normalize template sections.
   * @param {Array} sections
   * @returns {Array}
   */
  #normalizeSections(sections) {
    if (!Array.isArray(sections)) return [];

    return sections.map((section, index) => ({
      id: section?.id || this.#generateId(),
      title: this.#cleanString(section?.title),
      description: this.#cleanString(section?.description),
      order: Number.isFinite(section?.order) ? section.order : index,
      elements: this.#normalizeElements(section?.elements),
    }));
  }

  /**
   * Normalize section elements.
   * @param {Array} elements
   * @returns {Array}
   */
  #normalizeElements(elements) {
    if (!Array.isArray(elements)) return [];

    return elements.map((element) => ({
      id: element?.id || this.#generateId(),
      name: this.#cleanString(element?.name),
      label: this.#cleanString(element?.label),
      fieldType: this.#cleanString(element?.fieldType || "text"),
      unit: this.#cleanString(element?.unit),
      scope: this.#cleanString(element?.scope),
      isRequired: this.#normalizeBoolean(element?.isRequired, false),
      defaultValue:
        element?.defaultValue !== undefined ? element.defaultValue : null,
      calculationRule: element?.calculationRule ?? null,
      taxonomyDatapointId: this.#cleanString(element?.taxonomyDatapointId),
    }));
  }

  /**
   * Normalize taxonomy bindings.
   * @param {Array} taxonomyBindings
   * @returns {Array}
   */
  #normalizeTaxonomyBindings(taxonomyBindings) {
    if (!Array.isArray(taxonomyBindings)) return [];

    return taxonomyBindings.map((binding) => ({
      taxonomyUid: this.#cleanString(binding?.taxonomyUid),
      technicalId: this.#cleanString(binding?.technicalId),
      datapointId: this.#cleanString(binding?.datapointId),
      label: this.#cleanString(binding?.label),
      legalReference: this.#cleanString(binding?.legalReference),
      isMandatory: this.#normalizeBoolean(binding?.isMandatory, false),
    }));
  }

  /**
   * Deep-clone sections and regenerate nested IDs.
   * @param {Array} sections
   * @returns {Array}
   */
  #cloneSectionsWithNewIds(sections) {
    if (!Array.isArray(sections)) return [];

    return sections.map((section, sectionIndex) => ({
      ...section,
      id: this.#generateId(),
      order: Number.isFinite(section?.order) ? section.order : sectionIndex,
      elements: Array.isArray(section?.elements)
        ? section.elements.map((element) => ({
            ...element,
            id: this.#generateId(),
          }))
        : [],
    }));
  }

  /**
   * Throw a useful error if validation failed.
   * @param {Object} validation
   */
  #throwIfInvalid(validation) {
    if (!validation || validation.isValid) return;

    const errors = Array.isArray(validation.errors) ? validation.errors : [];
    const message =
      errors.length > 0
        ? errors.join(" | ")
        : "Template validation failed.";

    throw new Error(message);
  }

  /**
   * Prevent duplicate template names.
   * @param {string} candidateName
   * @param {Array} existingTemplates
   * @param {string|null} ignoreTemplateId
   */
  #ensureNameIsNotDuplicated(
    candidateName,
    existingTemplates = [],
    ignoreTemplateId = null,
  ) {
    const cleanCandidate = this.#cleanString(candidateName).toLowerCase();

    if (!cleanCandidate) return;

    const duplicate = existingTemplates.find((template) => {
      const sameId = ignoreTemplateId && template?.id === ignoreTemplateId;
      const sameName =
        this.#cleanString(template?.name).toLowerCase() === cleanCandidate;

      return !sameId && sameName;
    });

    if (duplicate) {
      throw new Error(`A template named "${candidateName}" already exists.`);
    }
  }

  /**
   * Build a friendly duplicate name.
   * @param {string} sourceName
   * @returns {string}
   */
  #buildCopyName(sourceName) {
    const safeName = this.#cleanString(sourceName || "Untitled Template");
    return `${safeName} (Copy)`;
  }

  /**
   * Guard: root file context required.
   * Expected shape:
   * {
   *   bucketName: string,
   *   fileName: string,
   *   filePath: string,
   *   folderName: string
   * }
   *
   * @param {Object} rootFileContext
   */
  #requireRootFileContext(rootFileContext) {
    if (!rootFileContext || typeof rootFileContext !== "object") {
      throw new Error("Root file context is required.");
    }

    if (!rootFileContext.fileName || !String(rootFileContext.fileName).trim()) {
      throw new Error("Root file context must include a fileName.");
    }

    if (!rootFileContext.folderName || !String(rootFileContext.folderName).trim()) {
      throw new Error("Root file context must include a folderName.");
    }
  }

  /**
   * Guard: template ID required.
   * @param {string} templateId
   */
  #requireTemplateId(templateId) {
    if (!templateId || typeof templateId !== "string" || !templateId.trim()) {
      throw new Error("Template ID is required.");
    }
  }

  /**
   * Guard: template payload required.
   * @param {Object} templateDto
   */
  #requireTemplatePayload(templateDto) {
    if (!templateDto || typeof templateDto !== "object") {
      throw new Error("Template payload is required.");
    }
  }

  /**
   * Normalize version.
   * @param {*} value
   * @returns {number}
   */
  #normalizeVersion(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /**
   * Normalize booleans with fallback.
   * @param {*} value
   * @param {boolean} fallback
   * @returns {boolean}
   */
  #normalizeBoolean(value, fallback = false) {
    if (typeof value === "boolean") return value;
    return fallback;
  }

  /**
   * Clean string-ish values.
   * @param {*} value
   * @returns {string}
   */
  #cleanString(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }

  /**
   * Generate IDs safely.
   * @returns {string}
   */
  #generateId() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }

    return `tmpl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * ISO timestamp helper.
   * @returns {string}
   */
  #nowIso() {
    return new Date().toISOString();
  }
}

export default TemplateManagementService;